import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProductUnitService from '../../services/ProductUnitService';
import RestaurantMenuItemService from '../../services/restaurant/RestaurantMenuItemService';
import RestaurantRawMaterialService from '../../services/restaurant/RestaurantRawMaterialService';
import RestaurantRecipeService from '../../services/restaurant/RestaurantRecipeService';
import RestaurantUnitConversionService from '../../services/restaurant/RestaurantUnitConversionService';
import { AppDispatch, RootState } from '../../store/store';
import {
  addRecipeIngredient,
  appendRecipeMenuItem,
  closeRecipeModal,
  moveRecipeIngredient,
  openRecipeCreate,
  openRecipeEdit,
  removeRecipeIngredient,
  setRecipeLoading,
  setRecipeMenuItems,
  setRecipeOptions,
  setRecipeOptionsLoading,
  setRecipePage,
  setRecipeSaving,
  updateRecipeField,
  updateRecipeIngredient,
} from '../../store/slices/restaurant/restaurantRecipeSlice';
import {
  RestaurantRecipeDTO,
  RestaurantRecipeFilters,
  RestaurantRecipeForm,
  RestaurantRecipeIngredientForm,
  RestaurantPageRequest,
} from '../../types/restaurant/RestaurantRecipeTypes';
import {
  RestaurantMenuItemDTO,
  RestaurantMenuItemForm,
} from '../../types/restaurant/RestaurantMenuTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantRecipes = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantRecipe);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const requestId = useRef(0);

  const loadOptions = async () => {
    if (!branchId) return;
    dispatch(setRecipeOptionsLoading(true));
    try {
      const [menuItems, rawMaterials, unitsResponse, conversions] =
        await Promise.all([
          RestaurantMenuItemService.listFiltered(branchId, {
            page: 0,
            size: 50,
            sort: ['displayOrder,asc', 'id,asc'],
            filters: {
              search: null,
              categoryId: null,
              available: true,
              featured: null,
              active: true,
            },
          }),
          RestaurantRawMaterialService.listFiltered(branchId, {
            page: 0,
            size: 20,
            sort: ['name,asc', 'id,asc'],
            filters: {
              search: null,
              categoryId: null,
              unitId: null,
              supplierId: null,
              active: true,
            },
          }),
          ProductUnitService.getDropdown(),
          RestaurantUnitConversionService.list({ activeOnly: true }),
        ]);
      const units = unitsResponse?.data ?? unitsResponse ?? [];
      dispatch(
        setRecipeOptions({
          menuItems: menuItems.content,
          rawMaterials: rawMaterials.content,
          units,
          conversions: conversions.data ?? [],
        })
      );
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRecipeOptionsLoading(false));
    }
  };

  const load = async (
    request: RestaurantPageRequest<RestaurantRecipeFilters>
  ) => {
    if (!branchId) return;
    const currentRequest = ++requestId.current;
    dispatch(setRecipeLoading(true));
    try {
      const page = await RestaurantRecipeService.listFiltered(
        branchId,
        request
      );
      if (currentRequest !== requestId.current) return;
      dispatch(setRecipePage(page));
      return page;
    } catch (error) {
      if (currentRequest === requestId.current) handle(error);
    } finally {
      if (currentRequest === requestId.current) {
        dispatch(setRecipeLoading(false));
      }
    }
  };

  const edit = async (item: RestaurantRecipeDTO) => {
    if (!branchId) return;
    try {
      const response = await RestaurantRecipeService.get(branchId, item.id);
      const detail = response.data;
      dispatch(
        openRecipeEdit({
          id: detail.id,
          menuItemId: detail.menuItemId,
          name: detail.name,
          description: detail.description ?? '',
          yieldQuantity: detail.yieldQuantity,
          yieldUnitId: detail.yieldUnitId,
          inventoryMode: detail.inventoryMode,
          active: detail.active,
          ingredients: [...detail.ingredients]
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map((ingredient) => ({
              key: ingredient.id,
              rawMaterialId: ingredient.rawMaterialId,
              quantity: ingredient.quantity,
              unitId: ingredient.unitId,
              notes: ingredient.notes ?? '',
              orderIndex: ingredient.orderIndex,
            })),
        })
      );
    } catch (error) {
      handle(error);
    }
  };

  const save = async (
    request: RestaurantPageRequest<RestaurantRecipeFilters>,
    onEmptyPage: () => void
  ) => {
    if (!branchId || state.saving) return;
    dispatch(setRecipeSaving(true));
    try {
      const response = state.form.id
        ? await RestaurantRecipeService.update(
            branchId,
            state.form.id,
            state.form
          )
        : await RestaurantRecipeService.create(branchId, state.form);
      show(
        response.type || 'success',
        response.title || 'Receta guardada',
        response.message || 'La receta se guardó correctamente.'
      );
      dispatch(closeRecipeModal());
      const page = await load(request);
      if (page?.content.length === 0 && request.page > 0) onEmptyPage();
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRecipeSaving(false));
    }
  };

  const deactivate = (
    item: RestaurantRecipeDTO,
    request: RestaurantPageRequest<RestaurantRecipeFilters>,
    onEmptyPage: () => void
  ) =>
    confirm({
      title: 'Desactivar receta',
      message: `¿Desea desactivar la receta “${item.name}”?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        if (!branchId) return;
        try {
          const response = await RestaurantRecipeService.deactivate(
            branchId,
            item.id
          );
          show(
            response.type || 'success',
            response.title || 'Receta desactivada',
            response.message || 'La receta se desactivó correctamente.'
          );
          const page = await load(request);
          if (page?.content.length === 0 && request.page > 0) onEmptyPage();
        } catch (error) {
          handle(error);
        }
      },
    });

  // =====================================================
  // 🔹 Alta de ítem de menú al vuelo (desde el form de receta)
  // =====================================================

  const reloadMenuItems = async () => {
    if (!branchId) return;
    try {
      const page = await RestaurantMenuItemService.listFiltered(branchId, {
        page: 0,
        size: 50,
        sort: ['displayOrder,asc', 'id,asc'],
        filters: {
          search: null,
          categoryId: null,
          available: true,
          featured: null,
          active: true,
        },
      });
      dispatch(setRecipeMenuItems(page.content));
    } catch (error) {
      handle(error);
    }
  };

  const createMenuItemInline = async (input: {
    productId: number;
    categoryId: number;
    displayName?: string;
  }): Promise<RestaurantMenuItemDTO | null> => {
    if (!branchId) return null;
    try {
      const form: RestaurantMenuItemForm = {
        productId: input.productId,
        categoryId: input.categoryId,
        displayName: input.displayName?.trim() ?? '',
        description: '',
        preparationTimeMinutes: '',
        preparationStationId: '',
        available: true,
        featured: false,
        displayOrder: 1,
      };
      const response = await RestaurantMenuItemService.create(branchId, form);
      dispatch(appendRecipeMenuItem(response.data));
      show(
        response.type || 'success',
        response.title || 'Ítem de menú creado',
        response.message ||
          `${response.data.displayName || response.data.productName} quedó listo para la receta.`
      );
      return response.data;
    } catch (error) {
      handle(error);
      return null;
    }
  };

  const updateField = <
    K extends keyof Omit<RestaurantRecipeForm, 'ingredients'>
  >(
    field: K,
    value: RestaurantRecipeForm[K]
  ) => dispatch(updateRecipeField({ field, value }));

  const updateIngredient = <
    K extends keyof Omit<RestaurantRecipeIngredientForm, 'key'>
  >(
    key: number,
    field: K,
    value: RestaurantRecipeIngredientForm[K]
  ) => dispatch(updateRecipeIngredient({ key, field, value }));

  return {
    ...state,
    load,
    loadOptions,
    edit,
    save,
    deactivate,
    updateField,
    updateIngredient,
    reloadMenuItems,
    createMenuItemInline,
    openCreate: () => dispatch(openRecipeCreate()),
    closeModal: () => dispatch(closeRecipeModal()),
    addIngredient: () => dispatch(addRecipeIngredient()),
    removeIngredient: (key: number) => dispatch(removeRecipeIngredient(key)),
    moveIngredient: (key: number, direction: -1 | 1) =>
      dispatch(moveRecipeIngredient({ key, direction })),
  };
};

export default useRestaurantRecipes;
